import { HTMLAttributes } from 'react';
import { classNames } from '@vkontakte/vkjs';
import { ButtonGroup, Caption, Footnote, Title, useColorScheme } from '@vkontakte/vkui';
import { arrify } from '@djs';
import { createCompoundComponent } from '@djs/react';
import { useCheckDesktop, useVKUIPlatform } from '@dui/hooks';
import { MessageBubbleAttachVoice } from '@dui/MessageBubbleAttachVoice';
import { MessageBubbleIndicator } from '../MessageBubbleIndicator';
import { MessageBubbleAttachImage } from '../MessageBubbleAttachImage';
import { MessageBubbleAttachVideo } from '../MessageBubbleAttachVideo';
import { MessageBubbleAttachButton } from '../MessageBubbleAttachButton';
import { MessageBubbleAttachLink } from '../MessageBubbleAttachLink';
import { MessageBubbleAttachReply } from '../MessageBubbleAttachReply';
import styles from './MessageBubble.module.css';

export interface MessageBubbleProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  // TODO придумать названиям incoming_* / outgoing_* альтернативные абстрактные.
  // Это просто вариант расскраски баббла, сам он не знает о его использовании.
  /** Цветовой стиль */
  mode?: 'incoming' | 'outgoing_bright' | 'advice' | 'question' | 'super_like' | 'super_message';
  /** Заголовок сообщения. Следует указывать имя отправителя */
  header?: React.ReactNode;
  /** Контент между header и top. Рекомендуется использовать MessageBubble.AttachReply */
  reply?: React.ReactNode;
  /**
   * Текстовое тело сообщения.
   * Рекомендуется строку или использовать обертки FormatTextContent или FormatWikiContent
   */
  children?: React.ReactNode;
  /** Стиль отображения текстового тела сообщения */
  childrenType?: 'primary' | 'emoji_medium' | 'emoji_large';
  /** Контент в правой нижней области сообщения. Следует использовать MessageBubbleAside */
  aside?: React.ReactNode;
  /**
   * Дополнительный контент с изображениями.
   * Рекомендуется использовать MessageBubble.AttachImage и MessageBubble.AttachVideo
   */
  top?: React.ReactNode;
  /**
   * Дополнительный контент с аттачами.
   * Рекомендуется использовать MessageBubble.AttachLink, MessageBubble.AttachButton и MessageBubble.AttachVoice.
   * Если вложений несколько, то MessageBubble.AttachLink рекомендуется ставить выше, а MessageBubble.AttachVoice ниже.
   */
  bottom?: React.ReactNode;

  testId?: string;

  contentTestId?: string;

  getRootRef?: React.RefObject<HTMLDivElement | null>;

  /** Компактный режим отображения дополнительного контента с аттачами */
  isBottomCompact?: boolean;

  /** Не разрешать aside вставать правее children в один ряд */
  isAsideStrictBottom?: boolean;
}

/**
 * Базовый компонент сообщения
 */
export const MessageBubble = createCompoundComponent(
  function MessageBubble(props: MessageBubbleProps) {
    const {
      className,
      mode = 'incoming',
      header,
      reply,
      children,
      childrenType = 'primary',
      top,
      bottom,
      aside,
      testId,
      contentTestId,
      getRootRef,
      isBottomCompact,
      isAsideStrictBottom,
      ...restProps
    } = props;
    const platform = useVKUIPlatform();
    const isDesktop = useCheckDesktop();
    const appearance = useColorScheme();

    const hasTop = Boolean(top && arrify(top).length > 0);
    const hasBottom = Boolean(bottom && arrify(bottom).length > 0);

    const isChildrenTypeEmoji = childrenType === 'emoji_medium' || childrenType === 'emoji_large';
    const isEmojiOnly = Boolean(children && isChildrenTypeEmoji && !hasTop && !hasBottom && !header);
    const isTopOnly = Boolean(hasTop && !children && !reply);

    const topEmptyBefore = Boolean(hasTop && !header && !reply);
    const topEmptyAfter = Boolean(hasTop && !children && !hasBottom);

    const isAsideInsideChildren = !isAsideStrictBottom && children && !hasBottom;

    const asideContent = aside && (
      <>
        <div className={styles.aside}>{aside}</div>
        <div className={styles.asideClearfix} />
      </>
    );

    return (
      <div
        data-testid={testId}
        ref={getRootRef}
        className={classNames(
          styles.root,
          styles[`root__${platform}`],
          styles[`root__appearance_${appearance}`],
          styles[`root__mode_${mode}`],
          styles[`root__children_type_${childrenType}`],
          hasTop && styles.root__has_top,
          topEmptyBefore && styles.root__top_empty_before,
          topEmptyAfter && styles.root__top_empty_after,
          isEmojiOnly && styles.root__emoji_only,
          !isAsideInsideChildren && styles.root__aside_outside_children,
          isTopOnly && styles.root__top_only,
          isBottomCompact && bottom && styles.root__bottom_compact,
          className,
        )}
        {...restProps}
      >
        {header && (
          <Caption weight="2" level="2" className={styles.header} useAccentWeight>
            {header}
          </Caption>
        )}
        {reply && <div className={styles.reply}>{reply}</div>}
        {hasTop && <div className={styles.top}>{top}</div>}
        {children && (
          <div className={styles.children}>
            {isDesktop ? (
              <Footnote data-testid={contentTestId} className={styles.content}>
                {children}
              </Footnote>
            ) : (
              <Title data-testid={contentTestId} level="3" weight="3" className={styles.content} useAccentWeight>
                {children}
              </Title>
            )}
            {isAsideInsideChildren && asideContent}
          </div>
        )}
        {hasBottom && (
          <ButtonGroup mode="vertical" gap="s" className={styles.bottom} stretched>
            {bottom}
          </ButtonGroup>
        )}
        {!isAsideInsideChildren && asideContent}
      </div>
    );
  },
  {
    Indicator: MessageBubbleIndicator,
    AttachImage: MessageBubbleAttachImage,
    AttachVideo: MessageBubbleAttachVideo,
    AttachButton: MessageBubbleAttachButton,
    AttachLink: MessageBubbleAttachLink,
    AttachReply: MessageBubbleAttachReply,
    AttachVoice: MessageBubbleAttachVoice,
  },
);
